/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の関連チケットカード内の「バグチケットを紐付け」入力欄。Backlog MCP 等で別途起票したバグチケットを issue key か URL で受け、本件調査結果バグ（investigation_result_bug）として依頼に紐付ける。紐付けると起票分類が bug になりバグ転換率に計上される（起票ボタンを通らない起票もここで数えられる）。key / URL の解釈は ops-core parseBacklogIssueRef（MCP link_ops_bug_ticket と同じ規則）で、解釈できなければその場で弾き API の失敗はメッセージをそのまま見せる
 * @graph-connects useAddOpsRelatedTicket [calls] 本件調査結果バグとして関連チケットへ紐付け（POST）を実行
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { backlogIssueUrl, parseBacklogIssueRef } from '@cortex/ops-core';
import { useAddOpsRelatedTicket } from '../../../hooks/useOps.js';

/** OpsLinkBugTicketForm の props。 */
export interface OpsLinkBugTicketFormProps {
  /** 紐付け先の依頼 id（Firestore 発番であることは呼び出し側が確認済み）。 */
  requestId: string;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * バグチケット紐付けフォーム。入力を issue key に正規化してから送るので、API には常に正規の key と URL が届く
 * （画面と MCP で同じチケットが別の ref として二重に並ぶのを防ぐ）。
 * @graph-connects none
 */
export function OpsLinkBugTicketForm({ requestId, t }: OpsLinkBugTicketFormProps): ReactNode {
  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const addMutation = useAddOpsRelatedTicket();

  const onSubmit = (): void => {
    const issueKey = parseBacklogIssueRef(value);
    if (issueKey === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    addMutation.mutate(
      {
        id: requestId,
        input: {
          target: 'backlog',
          kind: 'investigation_result_bug',
          ref: issueKey,
          url: backlogIssueUrl(issueKey),
        },
      },
      {
        onSuccess: () => {
          setValue('');
        },
      },
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-1 border-t border-[var(--color-border)] pt-2"
    >
      <div className="flex gap-1.5">
        <Input
          value={value}
          placeholder={t('ops.detail.related.linkBug.placeholder')}
          aria-label={t('ops.detail.related.linkBug.label')}
          aria-invalid={invalid}
          className="min-w-0 flex-1 text-xs"
          onChange={(e) => {
            setValue(e.target.value);
            setInvalid(false);
          }}
        />
        <Button
          type="submit"
          size="sm"
          // 入力欄（text-xs）に高さと文字サイズを揃えた小さいボタン。何を紐付けるかは入力欄のラベルが示す。
          className="shrink-0 px-2 py-1 text-xs"
          disabled={value.trim() === '' || addMutation.isPending}
        >
          {t('ops.detail.related.linkBug.submit')}
        </Button>
      </div>
      {invalid && (
        <p role="alert" className="text-xs text-[var(--color-error)]">
          {t('ops.detail.related.linkBug.invalid')}
        </p>
      )}
      {addMutation.isError && (
        <p role="alert" className="text-xs text-[var(--color-error)]">
          {t('ops.detail.related.linkBug.failed')}
          {addMutation.error.message === '' ? '' : `: ${addMutation.error.message}`}
        </p>
      )}
    </form>
  );
}
